/**
 * Vincent B912 (Eric 2026-02-14 SPEC STRICK PROD) — Page admin de
 * maintenance Fireflies. Bouton one-shot pour purger tous les segments
 * « sans texte » persistés avant B911 (correctif du parser).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FirefliesMaintenancePage() {
  // Tâche 1381 (Eric 2026-06-30) — Page entièrement française.
  const { tr } = useLang();
  const [purging, setPurging] = useState(false);
  const [report, setReport] = useState(null);
  const [needConfirm, setNeedConfirm] = useState(false);
  // Vincent B914 (Eric 2026-02-14 SPEC STRICK PROD)
  const [reimporting, setReimporting] = useState(false);
  const [reimportReport, setReimportReport] = useState(null);
  const [needConfirmReimport, setNeedConfirmReimport] = useState(false);
  // Vincent B974 (Eric 2026-02-26 SPEC STRICK PROD)
  const [rerunning, setRerunning] = useState(false);
  const [rerunReport, setRerunReport] = useState(null);
  const [needConfirmRerun, setNeedConfirmRerun] = useState(false);

  const runPurge = async () => {
    if (purging) return;
    if (!needConfirm) {
      setNeedConfirm(true);
      return;
    }
    setNeedConfirm(false);
    setPurging(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/fireflies/purge-empty-segments`,
        {},
        { withCredentials: true },
      );
      setReport(data);
      const n = data?.total_empty_segments_removed || 0;
      const d = data?.total_docs_touched || 0;
      if (n === 0) {
        toast.success(tr("✓ Aucun segment vide trouvé — vos données sont propres.", "✓ No empty segment found — your data is clean."));
      } else {
        toast.success(`${n} segment(s) vide(s) purgé(s) sur ${d} document(s).`);
      }
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="fireflies-maintenance-page">
      <h1 className="text-2xl font-bold text-zinc-900 mb-2">{tr("🔥 Maintenance Fireflies", "🔥 Fireflies maintenance")}</h1>
      <p className="text-sm text-zinc-600 mb-5">
        {tr(
          "Utilitaires de nettoyage rétroactifs pour les transcriptions Fireflies importées avant le correctif du parseur (B911).",
          "Retroactive clean-up utilities for Fireflies transcripts imported before the parser fix (B911).",
        )}
      </p>

      <section className="bg-white border border-zinc-200 rounded p-4 mb-4" data-testid="fireflies-purge-empty-section">
        <h2 className="text-base font-semibold text-zinc-900 mb-1">
          {tr("Purger les segments sans texte", "Purge segments with no text")}
        </h2>
        <p className="text-xs text-zinc-600 mb-3">
          {tr(
            "Balaie les transcriptions secondaires (source Fireflies) et fusionnées de votre organisation, et supprime tous les segments dont le texte est vide. Sans risque : ces segments n'apportaient aucun signal à la fusion.",
            "Sweeps your organisation's secondary (Fireflies) and merged transcripts, and removes every segment whose text is empty. Risk-free: those segments carried no signal into the fusion.",
          )}
        </p>
        <button
          type="button"
          onClick={runPurge}
          disabled={purging}
          className={`text-sm font-semibold px-4 py-2 rounded text-white disabled:opacity-50 ${
            needConfirm ? "bg-red-700 hover:bg-red-800" : "bg-teal-700 hover:bg-teal-800"
          }`}
          data-testid="fireflies-purge-empty-btn"
        >
          {purging
            ? tr("⏳ Purge en cours…", "⏳ Purging…")
            : needConfirm
              ? tr("⚠️ Confirmer la purge", "⚠️ Confirm the purge")
              : tr("🧹 Purger maintenant", "🧹 Purge now")}
        </button>
        {needConfirm && !purging && (
          <button
            type="button"
            onClick={() => setNeedConfirm(false)}
            className="ml-2 text-sm font-semibold px-3 py-2 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
            data-testid="fireflies-purge-cancel-btn"
          >
            {tr("Annuler", "Cancel")}
          </button>
        )}
        {needConfirm && (
          <p className="text-[11px] text-red-700 mt-2" data-testid="fireflies-purge-confirm-hint">
            {tr(
              "Cliquez à nouveau pour confirmer. Cette opération est sans risque (les segments concernés étaient déjà vides).",
              "Click again to confirm. This operation is risk-free (the segments involved were already empty).",
            )}
          </p>
        )}
      </section>

      {report && (
        <section
          className="bg-teal-50 border border-teal-200 rounded p-4"
          data-testid="fireflies-purge-report"
        >
          <h3 className="text-sm font-semibold text-teal-900 mb-2">
            {tr("📋 Rapport de purge", "📋 Purge report")}
          </h3>
          <ul className="text-xs text-teal-900 space-y-0.5 mb-2">
            <li>
              Segments vides retirés :{" "}
              <span
                className="font-mono font-bold"
                data-testid="fireflies-purge-total-removed"
              >
                {report.total_empty_segments_removed}
              </span>
            </li>
            <li>
              Documents touchés :{" "}
              <span className="font-mono font-bold">
                {report.total_docs_touched}
              </span>
            </li>
          </ul>
          {(report.per_video || []).length > 0 && (
            <details className="text-xs text-teal-900">
              <summary className="cursor-pointer font-medium">{tr("Détail par document", "Detail by document")}</summary>
              <ol className="mt-2 space-y-0.5 max-h-72 overflow-y-auto">
                {(report.per_video || []).map((r, i) => (
                  <li
                    key={`${r.video_id}-${r.collection}-${i}`}
                    className="font-mono text-[10px] bg-white border border-teal-200 rounded px-2 py-1"
                  >
                    [{r.collection}] {r.video_id?.slice(0, 8)}… — {r.empty_removed} vide(s), {r.kept} gardé(s)
                  </li>
                ))}
              </ol>
            </details>
          )}
        </section>
      )}

      {/* Vincent B914 (Eric 2026-02-14 SPEC STRICK PROD) — Section
          « Réimporter Fireflies partout » : ré-exécute le parser sur
          les fireflies_transcript bruts et peuple la bannière B913 sur
          les vidéos importées avant le fix. */}
      <section
        className="bg-white border border-zinc-200 rounded p-4 mt-4"
        data-testid="fireflies-bulk-reimport-section"
      >
        <h2 className="text-base font-semibold text-zinc-900 mb-1">
          {tr("Réimporter Fireflies partout", "Re-import Fireflies everywhere")}
        </h2>
        <p className="text-xs text-zinc-600 mb-3">
          {tr(
            "Ré-exécute le parseur sur la transcription Fireflies brute de chaque vidéo de votre organisation. Réécrit les segments et le rapport d'import pour remplir la bannière de diagnostic rétroactivement. Ne relance pas la fusion.",
            "Re-runs the parser on the raw Fireflies transcript of every video in your organisation. Rewrites the segments and the import report so the diagnostic banner is filled in retroactively. Does not relaunch the fusion.",
          )}
        </p>
        <button
          type="button"
          onClick={async () => {
            if (reimporting) return;
            if (!needConfirmReimport) { setNeedConfirmReimport(true); return; }
            setNeedConfirmReimport(false);
            setReimporting(true);
            try {
              const { data } = await axios.post(
                `${API}/api/admin/fireflies/bulk-reimport`,
                {},
                { withCredentials: true },
              );
              setReimportReport(data);
              const n = data?.total_reparsed || 0;
              const e = data?.total_empty_segments_skipped || 0;
              toast.success(`${n} vidéo(s) ré-importée(s) — ${e} segment(s) vide(s) au total.`);
            } catch (err) {
              toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
            } finally { setReimporting(false); }
          }}
          disabled={reimporting}
          className={`text-sm font-semibold px-4 py-2 rounded text-white disabled:opacity-50 ${
            needConfirmReimport ? "bg-red-700 hover:bg-red-800" : "bg-violet-700 hover:bg-violet-800"
          }`}
          data-testid="fireflies-bulk-reimport-btn"
        >
          {reimporting
            ? tr("⏳ Ré-import en cours…", "⏳ Re-importing…")
            : needConfirmReimport
              ? tr("⚠️ Confirmer le ré-import", "⚠️ Confirm the re-import")
              : tr("🔁 Réimporter maintenant", "🔁 Re-import now")}
        </button>
        {needConfirmReimport && !reimporting && (
          <button
            type="button"
            onClick={() => setNeedConfirmReimport(false)}
            className="ml-2 text-sm font-semibold px-3 py-2 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
            data-testid="fireflies-bulk-reimport-cancel-btn"
          >
            {tr("Annuler", "Cancel")}
          </button>
        )}
        {reimportReport && (
          <div className="mt-3 bg-violet-50 border border-violet-200 rounded p-3" data-testid="fireflies-bulk-reimport-report">
            <ul className="text-xs text-violet-900 space-y-0.5">
              <li>
                Vidéos scannées :{" "}
                <span className="font-mono font-bold">{reimportReport.total_scanned}</span>
              </li>
              <li>
                Vidéos ré-parsées :{" "}
                <span className="font-mono font-bold" data-testid="fireflies-bulk-reimport-count">
                  {reimportReport.total_reparsed}
                </span>
              </li>
              <li>
                Segments vides détectés au total :{" "}
                <span className="font-mono font-bold">{reimportReport.total_empty_segments_skipped}</span>
              </li>
            </ul>

            {/* Vincent B918 (Eric 2026-02-14 SPEC STRICK PROD) — Import Diff View :
                tableau avant/après par vidéo, trié par delta décroissant
                (les vidéos qui perdent le plus de segments en haut).
                Badge rouge sur les vidéos qui perdent > 50 % — signale un
                fichier Fireflies corrompu ou une régression parser. */}
            {(reimportReport.per_video || []).length > 0 && (
              <details className="mt-3 text-xs text-violet-900" open>
                <summary className="cursor-pointer font-semibold text-violet-900">
                  📊 Diff par vidéo (avant / après)
                </summary>
                <div className="mt-2 max-h-80 overflow-y-auto border border-violet-200 rounded bg-white" data-testid="fireflies-diff-table-wrapper">
                  <table className="w-full text-[11px]" data-testid="fireflies-diff-table">
                    <thead className="bg-violet-100 text-violet-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold">{tr("Vidéo", "Video")}</th>
                        <th className="text-right px-2 py-1 font-semibold">{tr("Avant", "Before")}</th>
                        <th className="text-right px-2 py-1 font-semibold">{tr("Après", "After")}</th>
                        <th className="text-right px-2 py-1 font-semibold">Δ</th>
                        <th className="text-right px-2 py-1 font-semibold">{tr("Vides skippés", "Empties skipped")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...reimportReport.per_video]
                        .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0))
                        .map((r) => {
                          const before = r.kept_before ?? 0;
                          const after = r.kept_segments ?? 0;
                          const delta = r.delta ?? (after - before);
                          const lostMajor = before > 0 && after <= before / 2;
                          const videdByError = before > 0 && after === 0;
                          return (
                            <tr
                              key={r.video_id}
                              className={`border-t border-violet-100 ${
                                videdByError ? "bg-red-50" : lostMajor ? "bg-amber-50" : ""
                              }`}
                              data-testid={`fireflies-diff-row-${r.video_id}`}
                              data-vided={videdByError ? "true" : undefined}
                            >
                              <td className="px-2 py-1 font-medium text-zinc-900">
                                <a
                                  href={`/dashboard/videos/${r.video_id}`}
                                  className="hover:underline text-violet-800"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {(r.title || r.video_id || "").slice(0, 60)}
                                </a>
                                {videdByError && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold"
                                    data-testid={`fireflies-diff-badge-vided-${r.video_id}`}
                                  >
                                    {tr("⚠ VIDÉE", "⚠ EMPTIED")}
                                  </span>
                                )}
                                {lostMajor && !videdByError && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-600 text-white text-[9px] font-bold"
                                    data-testid={`fireflies-diff-badge-major-${r.video_id}`}
                                  >
                                    −50%
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-700">{before}</td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-900 font-semibold">{after}</td>
                              <td
                                className={`px-2 py-1 text-right font-mono font-bold ${
                                  delta < 0 ? "text-red-700" : delta > 0 ? "text-emerald-700" : "text-zinc-500"
                                }`}
                              >
                                {delta > 0 ? `+${delta}` : delta}
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-600">
                                {r.empty_segments_skipped ?? 0}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-[10px] text-violet-700">
                  Trié par delta croissant (pertes en haut). Cliquez le titre pour ouvrir la vidéo.
                </p>
              </details>
            )}
          </div>
        )}
      </section>

      {/* Vincent B974 (Eric 2026-02-26 SPEC STRICK PROD) — Section
          « Refaire toutes les fusions » : relance la fusion Whisper +
          Fireflies sur toutes les vidéos éligibles. Utile après un
          ré-import massif (B914) qui ne propage PAS la fusion. */}
      <section
        className="bg-white border border-zinc-200 rounded p-4 mt-4"
        data-testid="fireflies-bulk-rerun-fusion-section"
      >
        <h2 className="text-base font-semibold text-zinc-900 mb-1">
          {tr("Refaire toutes les fusions", "Redo every fusion")}
        </h2>
        <p className="text-xs text-zinc-600 mb-3">
          {tr(
            "Relance la fusion Whisper + Fireflies sur TOUTES les vidéos de votre organisation qui possèdent les deux transcriptions. Utile après un ré-import massif : le ré-import réécrit les segments mais NE relance PAS la fusion — les transcriptions fusionnées restent désynchronisées jusqu'à ce que vous cliquiez ici.",
            "Relaunches the Whisper + Fireflies fusion on EVERY video in your organisation that has both transcripts. Useful after a bulk re-import: the re-import rewrites the segments but does NOT relaunch the fusion — merged transcripts stay out of sync until you click here.",
          )}
        </p>
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="fireflies-bulk-rerun-fusion-warning">
          {tr(
            "⚠️ Opération lourde : chaque vidéo consomme des crédits GPT-5.2 pour la fusion. Les fusions sont exécutées l'une après l'autre en arrière-plan (1 à 3 min par vidéo). Un instantané est pris avant chaque re-fusion, pour pouvoir revenir en arrière depuis l'historique.",
            "⚠️ Heavy operation: each video consumes GPT-5.2 credits for the fusion. Fusions run one after another in the background (1 to 3 min per video). A snapshot is taken before each re-fusion so you can roll back from the history.",
          )}
        </p>
        <button
          type="button"
          onClick={async () => {
            if (rerunning) return;
            if (!needConfirmRerun) { setNeedConfirmRerun(true); return; }
            setNeedConfirmRerun(false);
            setRerunning(true);
            try {
              const { data } = await axios.post(
                `${API}/api/admin/videos/bulk-rerun-fusion`,
                {},
                { withCredentials: true },
              );
              setRerunReport(data);
              const n = data?.total_launched || 0;
              const s = data?.total_skipped || 0;
              if (n === 0) {
                toast.success(`Aucune fusion à relancer — ${s} vidéo(s) skippée(s).`);
              } else {
                toast.success(`${n} fusion(s) relancée(s) en arrière-plan (${s} skippée(s)).`);
              }
            } catch (err) {
              toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
            } finally { setRerunning(false); }
          }}
          disabled={rerunning}
          className={`text-sm font-semibold px-4 py-2 rounded text-white disabled:opacity-50 ${
            needConfirmRerun ? "bg-red-700 hover:bg-red-800" : "bg-purple-700 hover:bg-purple-800"
          }`}
          data-testid="fireflies-bulk-rerun-fusion-btn"
        >
          {rerunning
            ? "⏳ Relance en cours…"
            : needConfirmRerun
              ? tr("⚠️ Confirmer la relance", "⚠️ Confirm the relaunch")
              : tr("🔀 Refaire toutes les fusions", "🔀 Redo every fusion")}
        </button>
        {needConfirmRerun && !rerunning && (
          <button
            type="button"
            onClick={() => setNeedConfirmRerun(false)}
            className="ml-2 text-sm font-semibold px-3 py-2 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
            data-testid="fireflies-bulk-rerun-fusion-cancel-btn"
          >
            {tr("Annuler", "Cancel")}
          </button>
        )}
        {rerunReport && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded p-3" data-testid="fireflies-bulk-rerun-fusion-report">
            <ul className="text-xs text-purple-900 space-y-0.5">
              <li>
                Vidéos scannées :{" "}
                <span className="font-mono font-bold">{rerunReport.total_scanned}</span>
              </li>
              <li>
                Fusions relancées :{" "}
                <span className="font-mono font-bold" data-testid="fireflies-bulk-rerun-fusion-launched-count">
                  {rerunReport.total_launched}
                </span>
              </li>
              <li>
                Vidéos skippées (pas de Whisper ou pas de Fireflies) :{" "}
                <span className="font-mono font-bold" data-testid="fireflies-bulk-rerun-fusion-skipped-count">
                  {rerunReport.total_skipped}
                </span>
              </li>
            </ul>
            {(rerunReport.per_video || []).length > 0 && (
              <details className="mt-3 text-xs text-purple-900" open>
                <summary className="cursor-pointer font-semibold text-purple-900">
                  {tr("📊 Détail par vidéo", "📊 Detail by video")}
                </summary>
                <div className="mt-2 max-h-80 overflow-y-auto border border-purple-200 rounded bg-white" data-testid="fireflies-bulk-rerun-fusion-table-wrapper">
                  <table className="w-full text-[11px]" data-testid="fireflies-bulk-rerun-fusion-table">
                    <thead className="bg-purple-100 text-purple-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold">{tr("Vidéo", "Video")}</th>
                        <th className="text-left px-2 py-1 font-semibold">Statut</th>
                        <th className="text-right px-2 py-1 font-semibold">Whisper</th>
                        <th className="text-right px-2 py-1 font-semibold">Fireflies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...rerunReport.per_video]
                        .sort((a, b) => (a.status === "launched" ? -1 : 1) - (b.status === "launched" ? -1 : 1))
                        .map((r) => {
                          const launched = r.status === "launched";
                          const reasonLabel = {
                            no_whisper: tr("Pas de Whisper", "No Whisper"),
                            no_fireflies: tr("Pas de Fireflies", "No Fireflies"),
                            no_whisper_segments: tr("Whisper vide", "Whisper empty"),
                          }[r.reason] || (launched ? tr("Lancée", "Launched") : r.reason || tr("Ignorée", "Skipped"));
                          return (
                            <tr
                              key={r.video_id}
                              className={`border-t border-purple-100 ${launched ? "" : "bg-zinc-50"}`}
                              data-testid={`fireflies-bulk-rerun-fusion-row-${r.video_id}`}
                              data-status={r.status}
                            >
                              <td className="px-2 py-1 font-medium text-zinc-900">
                                <a
                                  href={`/dashboard/videos/${r.video_id}`}
                                  className="hover:underline text-purple-800"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {(r.title || r.video_id || "").slice(0, 60)}
                                </a>
                              </td>
                              <td className="px-2 py-1">
                                <span
                                  className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                    launched
                                      ? "bg-purple-600 text-white"
                                      : "bg-zinc-300 text-zinc-800"
                                  }`}
                                >
                                  {launched ? "✓ LANCÉE" : reasonLabel}
                                </span>
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-700">
                                {r.whisper_segments ?? "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-700">
                                {r.fireflies_segments ?? "—"}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-[10px] text-purple-700">
                  Les vidéos lancées sont en tête. Cliquez le titre pour ouvrir la vidéo et suivre l&apos;onglet Fusionnée.
                </p>
              </details>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
